import {
  BLOCKER_CLASS, classifyEntity, entityKeys,
} from './deliveryBlockers';
import { asText } from './performanceCards';
import { isUnsupportedInterestRateFinding } from './interestRateFindings';
import { isInactiveDiagnosticCampaign } from './diagnosticCampaignEligibility';
import { activeDiagnosticView } from './inactiveDiagnosticFindings';

/**
 * The diagnostic run's own findings, as cards in the Findings feed.
 *
 * WHAT THIS REPLACES. The delivery diagnosis used to render as its own block
 * BELOW the feed — "35 blocked entities across 7 campaigns · from the run
 * itself, not the suggestion cards" — one collapsed section per campaign. That
 * put the run's real content underneath the applyable subset of it, sorted by
 * nothing, while the feed above it said "ordered by priority". An entity that
 * physically cannot serve outranks most of what was printed above it, and the
 * reader had to scroll past the ranked list to find out.
 *
 * So the rows enter the same feed, ranked with everything else. None of them
 * carries an edit payload — the run puts those in `actionable_suggestions`,
 * which is where the applyable cards already come from — so every card built
 * here is advisory, and RecCard labels it so. `advisoryReason` names WHY there
 * is no button, which is the one thing an advisory card owes the reader.
 *
 * ON THE PRIORITY NUMBERS. A suggestion carries `scoring.priority_score`, which
 * the diagnosis blocks do not have. Ordering the two together needs one scale,
 * so the ladder below maps the vocabulary the diagnostic briefs DO declare —
 * severity HARD_BLOCKER/SOFT_BLOCKER, blocking-issue HIGH/MEDIUM/LOW, the
 * health and measurement verdicts, and the blocker classes in
 * deliveryBlockers.js — onto it. These are bands, not scores: nothing here is
 * claimed to be the agent's own priority_score, and what the card SHOWS is the
 * band (`priBand`), never the number. The ladder's only job is to put a
 * campaign that cannot deliver above a keyword tweak.
 *
 * WHY THESE RANK WHEN THE OTHER ADVISORY CARDS DO NOT.
 * mergePerformanceRecommendationCards folds the Recommendations tab's test plan
 * and Google review into its feed with `pri: null`, which sorts them last. That
 * is the same rule, not a different one: rank by what the run declared, and
 * declare nothing where it said nothing. A test plan carries no severity — it is
 * an experiment with a decision rule — so there is nothing to rank it by. A
 * delivery finding carries HARD_BLOCKER, or HIGH, or STALLED, which is the run
 * stating how badly this one matters.
 *
 * WHAT IS DELIBERATELY NOT A CARD:
 *   switched-off entities   "you paused this" is not a finding. Counted by the
 *                           census line, never listed — the same rule the old
 *                           section held to.
 *   thin entities           serving, just thinly. Not a delivery fault, and it
 *                           already produces its own suggestion cards.
 *   covered entities        an entity a suggestion card already names is the
 *                           same finding told twice, and the card is the better
 *                           half: it carries the fix and the apply button.
 *   HEALTHY verdicts        a campaign with nothing wrong has nothing to say.
 */

/**
 * The band ladder. Read top to bottom: this is the order a reader should meet
 * these findings in, and every entry names why it sits where it does.
 */
export const DIAGNOSIS_PRI = {
  // The campaign itself cannot deliver, or cannot be optimized toward its own
  // objective. Nothing about an individual ad group matters until this is true.
  CAMPAIGN_STOPPED: 95,
  // An entity that should be serving and is not, and somebody here can fix it.
  HARD_BLOCKER: 85,
  // A blocking issue the run raised at the campaign level, its own HIGH.
  ISSUE_HIGH: 80,
  SOFT_BLOCKER: 60,
  ISSUE_MEDIUM: 55,
  // A negative that blocks a positive keyword's own traffic. Self-inflicted and
  // reported by no status field, which is why the run looks for it.
  CONFLICT: 50,
  MEASUREMENT_AT_RISK: 45,
  ISSUE_LOW: 35,
  // Context rather than a fault: the campaign delivers, less than it should.
  HEALTH_DEGRADED: 30,
  // Real, and nobody can act today — Google is reviewing, a policy limits it,
  // bidding is learning. Bottom of the list on purpose.
  WAITING: 20,
};

/** The badge the card shows. The number above only decides the order. */
export function bandFor(pri) {
  if (pri >= DIAGNOSIS_PRI.ISSUE_HIGH) return 'high';
  if (pri >= DIAGNOSIS_PRI.MEASUREMENT_AT_RISK) return 'medium';
  return 'low';
}

const upper = (v) => String(v ?? '').trim().toUpperCase();

/** Name, kind and reasons, normalized across the families' field names. */
const entityName = (e) => asText(e.entity_name || e.asset_group_name
  || e.entity_id || e.asset_group_id) || 'unnamed entity';
const entityKind = (e) => asText(e.entity_type)
  || (e.asset_group_id || e.asset_group_name ? 'asset group' : '');
const entityId = (e) => asText(e.entity_id || e.asset_group_id) || null;
const blockerList = (e) => (Array.isArray(e.blockers) ? e.blockers : [])
  .filter((b) => b && upper(b) !== 'NONE')
  .map((b) => asText(b))
  .filter(Boolean);

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** "2.4%", "100%", 0.024, 2.4 → a fraction of campaign spend. Null when unreadable. */
export function parseShare(v) {
  if (v === null || v === undefined || v === '') return null;
  const text = String(v).trim();
  const n = Number(text.replace('%', ''));
  if (!Number.isFinite(n)) return null;
  if (text.endsWith('%')) return n / 100;
  return n > 1 ? n / 100 : n;
}

/**
 * True only when the record SHOWS the entity is not serving.
 *
 * Every BLOCKED row used to read "<entity> is not serving". On the 7 Oct export
 * 29 of 51 such rows carried `can_deliver: true`, and the Germany asset group
 * Overseas_Transfers — `can_deliver: false, HARD_BLOCKER`, but 575 impressions
 * and ₹648 of spend — was serving everywhere except YouTube. `can_deliver` and
 * `severity` are the model's colouring (see deliveryBlockers.js); impressions
 * are Google's count.
 *
 *   `serving` present    the backend's own verdict (entity_serving.py, #3635):
 *                        'not_serving' says so; 'serving' and 'limited' say it
 *                        delivered. 'unknown' (no numbers) falls through.
 *   otherwise            `can_deliver === false` AND either zero impressions, or
 *                        no impressions field at all on a HARD_BLOCKER row.
 */
export function entityNotServing(e) {
  if (!e || typeof e !== 'object') return false;
  const serving = servingOf(e);
  if (serving === 'not_serving') return true;
  if (serving === 'serving' || serving === 'limited') return false;
  if (e.can_deliver !== false) return false;
  const imps = num(e.impressions);
  if (imps === 0) return true;
  return imps === null && upper(e.severity) === 'HARD_BLOCKER';
}

/** The backend's serving verdict, lower-cased; '' when absent or unrecognised. */
const SERVING_VALUES = new Set(['serving', 'limited', 'not_serving', 'unknown']);
export function servingOf(e) {
  const v = String(e?.serving ?? '').trim().toLowerCase();
  return SERVING_VALUES.has(v) ? v : '';
}

/**
 * `limits: [{code, text}]` — what #3635 moves out of `blockers` on an entity
 * that delivered (NEGATIVE_KEYWORD_CONFLICT, NO_VIDEO_ASSET, …). Read wherever
 * `blockers` is, or the reason vanishes from the card once that PR lands.
 */
const limitList = (e) => (Array.isArray(e?.limits) ? e.limits : [])
  .filter((l) => l && typeof l === 'object' && asText(l.code))
  .map((l) => ({ code: asText(l.code), text: asText(l.text) || null }));

/**
 * Google's reason codes in the reader's words. The codes stay in the card's
 * evidence line; they are never the headline.
 */
const REASON_TEXT = {
  RARELY_SERVED: 'rarely served (low search volume)',
  WEAK_AD_STRENGTH: 'weak ad strength',
  SINGLE_ELIGIBLE_AD: 'only one eligible ad',
  NEGATIVE_KEYWORD_CONFLICT: 'a negative keyword blocks some of its traffic',
  UNDIAGNOSED_DELIVERY_FAILURE: 'low delivery, cause not identified',
  ASSET_GROUP_LIMITED: 'limited by Google',
  ADS_LIMITED_BY_POLICY: 'ads limited by policy',
  LIMITED_BY_POLICY: 'limited by policy',
  ADS_DISAPPROVED: 'ads disapproved',
  NO_ELIGIBLE_ADS: 'no eligible ads',
  NO_AUDIENCE_SIGNAL: 'no audience signal',
  COMPLIANCE_VIOLATION: 'compliance violation',
  GEO_LANGUAGE_MISMATCH: 'location and language do not match',
  THIN_KEYWORD_COVERAGE: 'thin keyword coverage',
  PENDING_REVIEW: 'pending Google review',
  ADS_PENDING_REVIEW: 'ads pending Google review',
  ADS_IN_REVIEW: 'ads in Google review',
  ASSET_GROUP_PENDING_REVIEW: 'pending Google review',
  LEARNING: 'bid strategy learning',
  LEARNING_LIMITED: 'bid strategy learning (limited)',
  // #3635's limit codes.
  NO_VIDEO_ASSET: 'no video, can’t serve on YouTube',
  BELOW_RECOMMENDED_ASSETS: 'fewer assets than Google recommends',
};

/** MISSING_REQUIRED_ASSETS says which assets in `missing_assets`. */
function missingAssetsText(e) {
  const missing = (Array.isArray(e?.missing_assets) ? e.missing_assets : [])
    .map((m) => String(asText(m) || '').toLowerCase());
  if (missing.some((m) => m.includes('video'))) return 'no video, can’t serve on YouTube';
  if (missing.some((m) => m.includes('logo'))) return 'no logo';
  if (missing.some((m) => m.includes('headline'))) return 'too few headlines';
  if (missing.some((m) => m.includes('image'))) return 'too few images';
  return 'missing required assets';
}

/** One code → its reader text; an unknown code is humanised, never shown raw. */
function reasonText(code, e) {
  const key = upper(code);
  if (key === 'MISSING_REQUIRED_ASSETS') return missingAssetsText(e);
  if (REASON_TEXT[key]) return REASON_TEXT[key];
  return String(code).trim().toLowerCase().replace(/_+/g, ' ');
}

/** Every reason code on the row: its `limits` first, then its `blockers`. */
const reasonCodes = (e) => {
  const out = [];
  [...limitList(e).map((l) => l.code), ...blockerList(e)].forEach((c) => {
    if (!out.some((x) => upper(x) === upper(c))) out.push(c);
  });
  return out;
};

/**
 * The entity's reasons in reader words, most specific first, de-duplicated.
 * Built from `limits` (#3635) and `blockers` both, so the reason survives the
 * backend moving a code from one to the other.
 */
export function readableReasons(e) {
  const codes = reasonCodes(e);
  // A missing asset says more about WHY delivery is limited than "weak ad
  // strength" does — the strength is the consequence — so it leads.
  const ASSET_GAPS = ['MISSING_REQUIRED_ASSETS', 'NO_VIDEO_ASSET'];
  const missingFirst = (c) => (ASSET_GAPS.includes(upper(c)) ? 0 : 1);
  const out = [];
  [...codes].sort((a, b) => missingFirst(a) - missingFirst(b)).forEach((code) => {
    const text = reasonText(code, e);
    if (text && !out.includes(text)) out.push(text);
  });
  return out;
}

/**
 * The run's own delivery measurements, for quantifying a DEGRADED read.
 *
 * The diagnosis block (`health`) carries counts of entities, never a size of
 * gap. The same run's result does: the auction evidence the recommendation
 * guards read (impression share lost to budget and to rank — Search only) and
 * the pacing rollup (daily budget, recent daily spend). usePerformance attaches
 * this to each diagnosis as `deliveryEvidence`. Null when the run has none.
 */
export function deliveryEvidence(result) {
  const analysis = result?.details?.analysis || {};
  const rec = analysis.recommendation_evidence || {};
  const auction = rec.auction && rec.auction.available !== false ? rec.auction : {};
  const pacing = result?.analysis_inputs?.pacing_rollup || result?.analysis_data?.pacing_rollup || {};
  const out = {
    budgetLostIS: num(auction.search_budget_lost_impression_share),
    rankLostIS: num(auction.search_rank_lost_impression_share),
    dailyBudget: num(rec.daily_budget ?? pacing.daily_budget),
    recentDailySpend: num(pacing.recent_daily_spend ?? pacing.recent_rate?.recent_daily_spend),
    // Days the campaign hit its budget cap — a gap PMax can show, where the
    // Search-only impression-share fields are absent.
    cappedShare: num(pacing.budget_cap_behaviour?.share_of_days_capped),
    daysCapped: num(pacing.budget_cap_behaviour?.days_at_budget_cap),
    daysMeasured: num(pacing.budget_cap_behaviour?.days_measured),
  };
  return Object.values(out).some((v) => v !== null) ? out : null;
}

/** The bars a DEGRADED read must clear to count as a material gap. */
export const HEALTH_GAP = { LOST_IS: 0.20, SPEND_OF_BUDGET: 0.80, CAPPED_SHARE: 0.5, MIN_DAYS: 3 };

/** A health-block field that states a gap: `spend_gap`, `lost_impressions`, … */
const STATED_GAP = /(^|_)(gap|lost|drop|decline|fall)(_|$)/i;

/**
 * Whether a campaign's DEGRADED read quantifies a material gap, and in what
 * words. `{ material, parts }`; `parts` are reader text, most telling first.
 *
 *   impression share lost to budget ≥ 20%        auction evidence
 *   impression share lost to rank   ≥ 20%        auction evidence
 *   spend < 80% of daily budget on a campaign     pacing rollup + Google's
 *     Google reports BUDGET_CONSTRAINED           BUDGET_CONSTRAINED reason
 *   budget-capped on ≥ half of ≥ 3 measured days  pacing rollup (PMax has this)
 *   a numeric gap the health block states         `*_gap` / `lost_*` / `*_drop`
 *                                                 / `*_decline` fields
 *
 * A recent daily spend of 0 is read as "not measured", not as a gap: the
 * pacing rollup writes 0 on Search campaigns that plainly spent (the 7 Oct
 * runs), and a campaign that truly spent nothing is STALLED, not DEGRADED.
 * Percentages are rounded integers, so nothing here depends on locale.
 */
export function healthGap(d) {
  const parts = [];
  const ev = d?.deliveryEvidence || {};
  const pct = (x) => `${Math.round(x * 100)}%`;
  if (ev.budgetLostIS !== null && ev.budgetLostIS !== undefined && ev.budgetLostIS >= HEALTH_GAP.LOST_IS) {
    parts.push(`${pct(ev.budgetLostIS)} of impressions lost to budget`);
  }
  if (ev.rankLostIS !== null && ev.rankLostIS !== undefined && ev.rankLostIS >= HEALTH_GAP.LOST_IS) {
    parts.push(`${pct(ev.rankLostIS)} of impressions lost to ad rank`);
  }
  const reasons = (Array.isArray(d?.serving?.primary_status_reasons) ? d.serving.primary_status_reasons : [])
    .map(upper);
  if (reasons.includes('BUDGET_CONSTRAINED') && ev.dailyBudget > 0 && ev.recentDailySpend > 0
    && ev.recentDailySpend < HEALTH_GAP.SPEND_OF_BUDGET * ev.dailyBudget) {
    parts.push(`spending ${pct(ev.recentDailySpend / ev.dailyBudget)} of its daily budget`);
  }
  // Budget-capped on most measured days: demand the budget turned away. The
  // measure Performance Max has, where impression share is Search-only.
  if (ev.cappedShare !== null && ev.cappedShare !== undefined && ev.cappedShare >= HEALTH_GAP.CAPPED_SHARE
    && (ev.daysMeasured === null || ev.daysMeasured === undefined || ev.daysMeasured >= HEALTH_GAP.MIN_DAYS)) {
    parts.push(ev.daysCapped !== null && ev.daysCapped !== undefined && ev.daysMeasured
      ? `budget-capped on ${ev.daysCapped} of ${ev.daysMeasured} days`
      : `budget-capped on ${pct(ev.cappedShare)} of days`);
  }
  Object.entries(d?.health || {}).forEach(([key, value]) => {
    const n = num(value);
    if (STATED_GAP.test(key) && n !== null && n > 0) {
      parts.push(`${key.replace(/_+/g, ' ')}: ${Number.isInteger(n) ? n : n.toFixed(2)}`);
    }
  });
  return { material: parts.length > 0, parts };
}

/** Quoted terms in a sentence: 'x', "x", ‘x’, “x”. */
export function quotedTerms(text) {
  const out = [];
  const re = /'([^']+)'|"([^"]+)"|‘([^’]+)’|“([^”]+)”/g;
  const str = String(text || '');
  let m = re.exec(str);
  while (m) {
    out.push((m[1] || m[2] || m[3] || m[4]).trim());
    m = re.exec(str);
  }
  return out;
}

/** An issue about the whole campaign rather than named groups. */
const WHOLE_CAMPAIGN = /\b(the|this) campaign\b|campaign[- ]level|\ball (\d+ )?(active |enabled )?(ad groups|asset groups)\b|BUDGET_CONSTRAINED/i;

/** "Mobile_Banking_App (1)" — a numbered copy of another group. */
export const COPY_NAME = /\(\d+\)\s*$/;

/**
 * What a campaign-level blocking issue is about, resolved against the
 * campaign's own entity rows: by the ids it lists (`affected_entities`,
 * `affected_asset_groups`, `affected_ad_groups`; "all" means every live one),
 * or else by the entity names it quotes in its text.
 *
 *   entities           how many rows matched
 *   wholeCampaign      the issue is about the campaign as a whole ("all",
 *                      "the campaign", "all 12 ad groups", BUDGET_CONSTRAINED)
 *   share              summed share of campaign spend of the matched rows
 *                      (a fraction; null when nothing matched or no share)
 *   copiesNeverServed  every matched row is a "(n)" copy with zero spend and
 *                      zero impressions — a duplicate that never went live
 */
function issueScope(b, entities, totalCost) {
  const ids = [b.affected_entities, b.affected_asset_groups, b.affected_ad_groups]
    .flatMap((x) => (Array.isArray(x) ? x : []))
    .map((x) => String(asText(x) || '').trim().toLowerCase()).filter(Boolean);
  let rows;
  if (ids.includes('all')) rows = entities.filter((e) => classifyEntity(e) !== BLOCKER_CLASS.OFF);
  else if (ids.length) {
    rows = entities.filter((e) => entityKeys(e).some((k) => ids.includes(k.replace(/^(id|name)::/, ''))));
  } else {
    const quoted = quotedTerms(b.issue).map((t) => t.toLowerCase());
    rows = entities.filter((e) => quoted.includes(String(entityName(e)).toLowerCase()));
  }
  const shareOf = (e) => parseShare(e.share_of_campaign_spend)
    ?? (num(e.cost) !== null && totalCost > 0 ? num(e.cost) / totalCost : null);
  const shares = rows.map(shareOf).filter((x) => x !== null);
  return {
    entities: rows.length,
    // Named explicitly (by id or quoted name), not by "all": the rows a dedupe
    // can fold into this issue. Not exported on the card.
    named: ids.includes('all') ? [] : rows,
    wholeCampaign: ids.includes('all') || WHOLE_CAMPAIGN.test(asText(b.issue) || ''),
    share: shares.length ? Math.min(1, shares.reduce((a, x) => a + x, 0)) : null,
    copiesNeverServed: rows.length > 0 && rows.every((e) => COPY_NAME.test(entityName(e))
      && num(e.cost) === 0 && num(e.impressions) === 0),
  };
}

/**
 * Whether an entity row becomes a card.
 *
 * BLOCKED and WAITING, as before (see deliveryBlockers.js). Plus one shape
 * #3635 creates: an entity the backend grounded as `serving: 'limited'` whose
 * codes now sit in `limits` rather than `blockers`. Such a row is carded ONLY
 * for a negative-keyword conflict or a policy limit — the two limits that cut
 * traffic the entity would otherwise get. Carding every limit (no video, below
 * recommended assets, a single eligible ad) added 66 cards to IDFC's 8 Oct
 * feed (148 → 214) and 13 to ABB's (73 → 86), nearly all repeating a campaign
 * issue or account review already in it.
 */
const LIMITED_CARD_CODE = (code) => upper(code) === 'NEGATIVE_KEYWORD_CONFLICT' || /POLIC/.test(upper(code));
function listable(e) {
  const cls = classifyEntity(e);
  if (cls === BLOCKER_CLASS.OFF) return false;
  if (cls === BLOCKER_CLASS.BLOCKED || cls === BLOCKER_CLASS.WAITING) return true;
  return servingOf(e) === 'limited' && limitList(e).some((l) => LIMITED_CARD_CODE(l.code));
}

/**
 * The entity ids an account review in the feed names. A review lists the
 * entities it covers in its evidence (ad_group_id, asset group ids, target
 * ids); Google Ads ids are account-unique, so a substring of the review's own
 * JSON is enough — names are not matched, they repeat across campaigns.
 */
function reviewText(feedCards) {
  return (Array.isArray(feedCards) ? feedCards : [])
    .filter((c) => c && c.route === 'diagnostics' && String(c.action || '').toLowerCase() === 'tracking')
    .map((c) => {
      try { return JSON.stringify(c.raw || {}); } catch (err) { return ''; }
    })
    .join('\n');
}

const namedByReview = (e, text) => {
  if (!text) return false;
  const id = String(asText(e.entity_id ?? e.asset_group_id ?? e.ad_group_id) || '').trim();
  return id.length >= 6 && text.includes(id);
};

/**
 * A listable row a card already names — the same finding told twice: a
 * suggestion card (deliveryBlockers.coveredByCard, widened to the `limits`
 * rows above), a blocking issue of the same campaign that names it, or an
 * account review that lists it.
 */
function covered(e, cardKeys, { issueRows = null, reviews = '' } = {}) {
  if (!listable(e)) return false;
  if (cardKeys && cardKeys.size && entityKeys(e).some((k) => cardKeys.has(k))) return true;
  // Named by a blocking issue in the same campaign, or by an account review.
  if (issueRows && issueRows.has(e)) return true;
  return namedByReview(e, reviews);
}

/** Entity rows the campaign's own blocking issues name explicitly. */
function issueNamedRows(d) {
  const entities = Array.isArray(d.entities) ? d.entities : [];
  const out = new Set();
  (Array.isArray(d.blockers) ? d.blockers : []).forEach((b) => {
    issueScope(b, entities, 0).named.forEach((e) => out.add(e));
  });
  return out;
}

/**
 * Why none of these has an Apply button, in the words the finding earns.
 *
 * Not one sentence for all of them: "Google is still reviewing it" and "the run
 * described this state without proposing an edit" are different answers, and a
 * reader who is told the wrong one goes looking for a button that will never
 * exist.
 */
const NO_APPLY = {
  entity: 'The run reports this entity’s state; the fix it names is a change to make, '
    + 'not an edit payload this page can execute. Applyable fixes arrive as their own cards.',
  waiting: 'Nobody can act on this from here — it clears when Google finishes, or when the '
    + 'policy or bid strategy resolves. It returns next run until then.',
  issue: 'A campaign-level finding, not a targeted edit: the run named the issue and the fix '
    + 'without an entity and value to apply them to.',
  conflict: 'Removing a negative keyword is not offered here — which of the two terms should '
    + 'win is the reader’s call, and the run only reports that they collide.',
  measurement: 'Conversion tracking is configured outside this tool, so there is nothing here '
    + 'to apply. The bidder keeps optimizing toward the signal it is given.',
  health: 'A read of the campaign as a whole, assembled from the findings below it rather '
    + 'than a change of its own.',
};

/**
 * One campaign's diagnosis → its cards.
 *
 * `seq` is a running counter across the whole account so every card gets a
 * distinct `index`: sortByPriority tie-breaks on it, and cards sharing one
 * would order arbitrarily between renders.
 */
function cardsForDiagnosis(d, { cardKeys, seq, reviews = '' }) {
  if (isInactiveDiagnosticCampaign(d)) return [];
  d = activeDiagnosticView(d);
  const issueRows = issueNamedRows(d);
  const cards = [];
  const camp = d.campaignId || null;
  const campName = d.campaignName || null;
  const base = (kind, key) => ({
    id: `${camp || 'acct'}-diagnosis-${kind}-${key}`,
    index: seq(),
    route: 'diagnostics',
    camp,
    campName,
    dest: d.destination || null,
    agent: 'Diagnostic',
    // Advisory, every one of them: the diagnosis blocks carry no edit payload.
    auto: false,
    // Fields RecCard reads. Absent ones render as nothing rather than as an
    // em-dash that would read like a measured zero — see the component.
    head: null,
    plain: null,
    cons: null,
    ctx: null,
    ev: null,
    me: null,
    coi: null,
    cur: null,
    rec: null,
    sar: null,
    ccy: null,
    conf: null,
    recur: null,
    applied: null,
    judge: null,
    judgeReason: null,
    tids: [],
    conflict: false,
    dupe: false,
    n: 1,
    nlev: 1,
    agents: [],
    elsewhere: [],
    // Marks this card as built from the run's diagnosis rather than from a
    // suggestion. The feed does not branch on it; the tests and the census do.
    fromDiagnosis: true,
  });

  /**
   * The campaign-level cards name their campaign IN THE TITLE.
   *
   * The old section grouped by campaign, so "Campaign is not delivering" was
   * unambiguous inside it. In an account-wide feed it is not: seven campaigns
   * produce seven identical titles, and the campaign chip sits at the other end
   * of the row. The entity cards never had the problem — an entity's own name is
   * its title — so the fix is to give these the same subject-first shape.
   */
  const subject = campName || 'This campaign';

  // ── the campaign as a whole ────────────────────────────────────────────────
  const health = upper(d.health?.verdict);
  const healthSummary = asText(d.health?.summary);
  const gap = healthGap(d);
  if (health && health !== 'HEALTHY') {
    const stalled = health === 'STALLED';
    const pri = stalled ? DIAGNOSIS_PRI.CAMPAIGN_STOPPED : DIAGNOSIS_PRI.HEALTH_DEGRADED;
    cards.push({
      ...base('health', 'campaign'),
      action: 'delivery_health',
      cat: 'status',
      tt: 'campaign',
      tn: campName,
      tid: camp,
      // A DEGRADED read says "below capacity" with no number. Where the run
      // measured the gap (healthGap), the title carries it and the one-liner
      // lists it — and findingTier.js leads only with those.
      head: stalled
        ? `${subject} is not delivering`
        : `${subject} is delivering below capacity${gap.parts.length ? `: ${gap.parts[0]}` : ''}`,
      cons: gap.parts.length ? gap.parts.join(' · ') : `delivery health ${health}`,
      plain: healthSummary || null,
      ev: healthSummary || null,
      pri,
      priBand: bandFor(pri),
      advisoryReason: NO_APPLY.health,
      healthGap: gap,
      // Google's campaign-level reasons, for findingTier.campaignGap.
      servingReasons: (Array.isArray(d.serving?.primary_status_reasons) ? d.serving.primary_status_reasons : [])
        .map((r) => asText(r)).filter(Boolean),
      raw: d.health || null,
    });
  }

  const measurement = upper(d.measurement?.verdict);
  if (measurement && measurement !== 'OK') {
    // BLOCKED means the bidder optimizes toward a signal that never arrives —
    // the campaign can spend its whole budget and still be uninstrumented, which
    // is why it ranks with a stopped campaign rather than with a soft finding.
    const blocked = measurement === 'BLOCKED';
    const pri = blocked
      ? DIAGNOSIS_PRI.CAMPAIGN_STOPPED
      : DIAGNOSIS_PRI.MEASUREMENT_AT_RISK;
    cards.push({
      ...base('measurement', 'campaign'),
      action: 'measurement_check',
      cat: 'measurement',
      tt: 'campaign',
      tn: campName,
      tid: camp,
      head: blocked
        ? `${subject} is bidding toward a conversion signal it never receives`
        : `${subject}’s conversion signal is at risk`,
      cons: `measurement ${measurement}`,
      plain: asText(d.measurement?.detail) || null,
      cur: asText(d.measurement?.conversion_tracking_status) || null,
      ev: [
        asText(d.measurement?.bidding_strategy)
          ? `Bidding strategy: ${asText(d.measurement.bidding_strategy)}`
          : null,
        asText(d.measurement?.conversion_tracking_status)
          ? `Conversion tracking: ${asText(d.measurement.conversion_tracking_status)}`
          : null,
      ].filter(Boolean).join(' · ') || null,
      pri,
      priBand: bandFor(pri),
      advisoryReason: NO_APPLY.measurement,
      raw: d.measurement || null,
    });
  }

  // ── the entities ──────────────────────────────────────────────────────────
  const entities = Array.isArray(d.entities) ? d.entities : [];
  // Campaign context for findingTier.js, which sees one card at a time: the
  // entity's share of campaign spend, and whether anything ELSE in the
  // campaign serves. Switched-off rows are not "something else serving".
  const totalCost = entities.reduce((sum, x) => sum + (num(x?.cost) || 0), 0);
  const serves = (x) => {
    if (classifyEntity(x) === BLOCKER_CLASS.OFF) return false;
    const v = servingOf(x);
    if (v === 'serving' || v === 'limited') return true;
    return !entityNotServing(x)
      && ((num(x?.impressions) || 0) > 0 || (num(x?.cost) || 0) > 0);
  };
  entities.forEach((e, i) => {
    const cls = classifyEntity(e);
    // OFF and THIN are never cards; see the file header. A covered entity is
    // the same finding as a card already in this feed.
    if (!listable(e)) return;
    if (covered(e, cardKeys, { issueRows, reviews })) return;

    const waiting = cls === BLOCKER_CLASS.WAITING;
    const reasons = reasonCodes(e);
    const limits = limitList(e);
    const recommended = (Array.isArray(e.missing_recommended_assets) ? e.missing_recommended_assets : [])
      .map((a) => asText(a)).filter(Boolean);
    const readable = readableReasons(e);
    const notServing = entityNotServing(e);
    const cost = num(e.cost);
    const share = parseShare(e.share_of_campaign_spend)
      ?? (cost !== null && totalCost > 0 ? cost / totalCost : null);
    const hard = upper(e.severity) === 'HARD_BLOCKER';
    const pri = waiting
      ? DIAGNOSIS_PRI.WAITING
      : (hard ? DIAGNOSIS_PRI.HARD_BLOCKER : DIAGNOSIS_PRI.SOFT_BLOCKER);
    const kind = entityKind(e);
    cards.push({
      ...base('entity', entityId(e) || `${entityName(e)}-${i}`),
      action: waiting ? 'delivery_waiting' : 'delivery_blocked',
      cat: 'status',
      tt: kind || null,
      tn: entityName(e),
      tid: entityId(e),
      // "Is not serving" only when the record shows it (entityNotServing);
      // otherwise the specific limit, in words. Two reasons at most in the
      // title — the rest are in the one-liner below it.
      head: waiting
        ? `${entityName(e)} is waiting on Google`
        : (notServing || !readable.length
          ? `${entityName(e)} is not serving`
          : `${entityName(e)} serves with limits: ${readable.slice(0, 2).join('; ')}`),
      // The reasons are the one-liner — what a reader scanning collapsed cards
      // needs to tell two blocked ad groups apart — in words, not codes.
      cons: readable.join(', ') || (waiting ? 'waiting on review' : 'cannot serve'),
      plain: asText(e.fix) || null,
      ev: [
        asText(e.evidence) || null,
        (Array.isArray(e.primary_status_reasons) ? e.primary_status_reasons : [])
          .map((r) => asText(r)).filter(Boolean).join(', ')
          ? `Google reports: ${(e.primary_status_reasons || []).map((r) => asText(r)).filter(Boolean).join(', ')}`
          : null,
        // #3635's limits carry their own sentence; a code without one is
        // rendered in words.
        limits.length
          ? `Limits: ${limits.map((l) => l.text || `${reasonText(l.code, e)}.`).join(' ')}`
          : null,
        recommended.length ? `Recommended, not required: ${recommended.join(', ')}` : null,
        // Google's own codes, kept for whoever looks the entity up in Ads.
        reasons.length ? `Reason codes: ${reasons.join(', ')}` : null,
      ].filter(Boolean).join(' — ') || null,
      // Read by findingTier.js. `share` is the reporting window's share of
      // campaign spend (a fraction); `otherServing` is whether any other
      // entity in this campaign serves.
      entityDelivery: {
        notServing,
        share,
        otherServing: entities.some((x) => x !== e && serves(x)),
      },
      pri,
      priBand: bandFor(pri),
      advisoryReason: waiting ? NO_APPLY.waiting : NO_APPLY.entity,
      // A waiting row has nothing to dismiss: it is a state, not a judgement,
      // and it returns next run until Google clears it. The old section gave it
      // no Dismiss for that reason and the feed keeps the rule.
      ...(waiting ? { noDismiss: true } : {}),
      raw: e,
    });
  });

  // ── the campaign-level blocking issues ────────────────────────────────────
  (Array.isArray(d.blockers) ? d.blockers : []).forEach((b, i) => {
    const sev = upper(b.severity);
    const pri = sev === 'HIGH'
      ? DIAGNOSIS_PRI.ISSUE_HIGH
      : (sev === 'LOW' ? DIAGNOSIS_PRI.ISSUE_LOW : DIAGNOSIS_PRI.ISSUE_MEDIUM);
    const affected = (Array.isArray(b.affected_entities) ? b.affected_entities : [])
      .map((x) => asText(x)).filter(Boolean);
    const scope = issueScope(b, entities, totalCost);
    cards.push({
      ...base('issue', i),
      action: 'blocking_issue',
      cat: 'status',
      tt: 'campaign',
      tn: campName,
      tid: camp,
      head: asText(b.issue) || 'Blocking issue',
      cons: sev ? `${sev.toLowerCase()} severity` : null,
      plain: asText(b.fix) || null,
      ev: affected.length
        ? `Affects ${affected.length} ${affected.length === 1 ? 'entity' : 'entities'}`
        : null,
      pri,
      priBand: bandFor(pri),
      advisoryReason: NO_APPLY.issue,
      // Read by findingTier.js: which of this campaign's entities the issue
      // names, their share of campaign spend, and whether they are all
      // never-served "(n)" copies.
      issueScope: scope,
      raw: b,
    });
  });

  // ── the negative-keyword conflicts ────────────────────────────────────────
  (Array.isArray(d.conflicts) ? d.conflicts : []).forEach((c, i) => {
    const negative = asText(c.negative);
    const blocks = (Array.isArray(c.blocks) ? c.blocks : [])
      .map((k) => asText(k)).filter(Boolean);
    cards.push({
      ...base('conflict', i),
      action: 'negative_keyword_conflict',
      cat: 'negative_keywords',
      tt: 'negative keyword',
      tn: negative || null,
      tid: null,
      head: negative
        ? `Negative “${negative}” blocks this campaign’s own keywords`
        : 'A negative keyword blocks this campaign’s own keywords',
      cons: blocks.length
        ? `blocks ${blocks.slice(0, 3).join(', ')}${blocks.length > 3 ? `, +${blocks.length - 3} more` : ''}`
        : (asText(c.match_type) ? `${asText(c.match_type)} match` : null),
      plain: asText(c.detail) || null,
      ev: [
        asText(c.match_type) ? `${asText(c.match_type)} match` : null,
        asText(c.source) ? `from ${asText(c.source)}` : null,
      ].filter(Boolean).join(' · ') || null,
      pri: DIAGNOSIS_PRI.CONFLICT,
      priBand: bandFor(DIAGNOSIS_PRI.CONFLICT),
      advisoryReason: NO_APPLY.conflict,
      raw: c,
    });
  });

  return cards;
}

/**
 * Every campaign's diagnosis → one flat list of feed cards.
 *
 * `cardKeys` is cardEntityKeys() over the suggestion cards already in the feed,
 * so an entity with a card does not also become an advisory row.
 */
export function diagnosisCards(diagnoses, cardKeys, feedCards = []) {
  let n = 0;
  // Offset past the suggestion indexes so a diagnosis card never ties with a
  // suggestion on BOTH pri and index — where the sort would be arbitrary.
  const seq = () => { n += 1; return 10_000 + n; };
  const reviews = reviewText(feedCards);
  return (Array.isArray(diagnoses) ? diagnoses : [])
    .flatMap((d) => cardsForDiagnosis(d, { cardKeys, seq, reviews }))
    .filter(card => !isUnsupportedInterestRateFinding(card));
}

/**
 * What the diagnosis holds that is NOT in the feed, for the census line.
 *
 * Said out loud for the reason the old header said it: a reader who counted
 * entities in Google and finds fewer here is owed the difference. `off` is
 * inventory somebody switched off on purpose; `covered` is entities a suggestion
 * card in this same feed already names.
 */
export function diagnosisCensus(diagnoses, cardKeys, feedCards = []) {
  let off = 0;
  let coveredN = 0;
  const reviews = reviewText(feedCards);
  (Array.isArray(diagnoses) ? diagnoses : []).forEach((d) => {
    if (isInactiveDiagnosticCampaign(d)) return;
    off += d.excludedEntityCount || 0;
    const issueRows = issueNamedRows(activeDiagnosticView(d));
    (Array.isArray(d.entities) ? d.entities : []).forEach((e) => {
      if (classifyEntity(e) === BLOCKER_CLASS.OFF) { off += 1; return; }
      if (covered(e, cardKeys, { issueRows, reviews })) coveredN += 1;
    });
  });
  return { off, covered: coveredN };
}

const diagnosisFindings = {
  DIAGNOSIS_PRI, bandFor, diagnosisCards, diagnosisCensus,
  entityNotServing, parseShare, readableReasons, servingOf, deliveryEvidence, healthGap, quotedTerms,
};

export default diagnosisFindings;
